import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import ApplicationLayoutBodyBottomBar from '@rio-cloud/rio-uikit/ApplicationLayoutBodyBottomBar';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router';

import { useHandleBookingState } from '#hooks/booking/useHandleBookingState.ts';

import { MissingPocketDriverDataSourceBannerStateProvider } from '../resources/MissingPocketDriverDataSourceBannerState.tsx';
import { BottomBarViewRefresh } from '../shared/BottomBarViewRefresh.tsx';
import { ErrorBoundary } from '../shared/ErrorBoundary.tsx';
import ErrorFallback from '../shared/ErrorFallback.tsx';
import { SidebarContextProvider } from '../shared/SidebarContext.tsx';
import AppHeader from './AppHeader.tsx';
import {
    type ApplicationLayoutBottomBarConfig,
    ApplicationLayoutBottomBarContext,
} from './ApplicationLayoutBottomBarContext.tsx';

const ANALYTICS_OVERLAY_PARAM = 'AnalyticsAnalysisOverlay';
let shouldShowAnalyticsOverlayFromUrl = false;

const AnalyticsAnalysisOverlay = lazy(() =>
    import('@rio-cloud/rio-uikit/AnalyticsAnalysisOverlay').then(module => ({
        default: module.AnalyticsAnalysisOverlay,
    }))
);

const getAnalyticsOverlaySearchParam = () => {
    const routeSearchValue = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get(ANALYTICS_OVERLAY_PARAM);
    const globalSearchValue = new URLSearchParams(window.location.search).get(ANALYTICS_OVERLAY_PARAM);

    return routeSearchValue ?? globalSearchValue;
};

const shouldEnableAnalyticsOverlay = () => {
    if (shouldShowAnalyticsOverlayFromUrl) {
        return true;
    }

    const searchValue = getAnalyticsOverlaySearchParam();
    if (searchValue === 'show') {
        shouldShowAnalyticsOverlayFromUrl = true;
        return true;
    }

    return false;
};

export const AppLayout = () => {
    const sidebarRef = useRef<HTMLDivElement>(null);
    const { isLoading, error, refetch } = useHandleBookingState();
    const [bottomBar, setBottomBar] = useState<ApplicationLayoutBottomBarConfig | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const bottomBarContextValue = useMemo(() => ({ setBottomBar }), []);
    const [showAnalyticsAnalysisOverlay, setShowAnalyticsAnalysisOverlay] = useState(shouldEnableAnalyticsOverlay);

    useEffect(() => {
        if (shouldEnableAnalyticsOverlay()) {
            setShowAnalyticsAnalysisOverlay(true);
        }

        if (searchParams.has(ANALYTICS_OVERLAY_PARAM)) {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.delete(ANALYTICS_OVERLAY_PARAM);
            setSearchParams(nextSearchParams, { replace: true });
        }

        const browserSearchParams = new URLSearchParams(window.location.search);
        if (browserSearchParams.has(ANALYTICS_OVERLAY_PARAM)) {
            browserSearchParams.delete(ANALYTICS_OVERLAY_PARAM);
            const nextSearch = browserSearchParams.toString();
            const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
            window.history.replaceState(window.history.state, '', nextUrl);
        }
    }, [searchParams, setSearchParams]);

    return (
        <MissingPocketDriverDataSourceBannerStateProvider>
            <ApplicationLayout>
                <ApplicationLayout.Header>
                    <AppHeader />
                </ApplicationLayout.Header>
                <ApplicationLayout.Sidebar className='right' ref={sidebarRef} />
                <SidebarContextProvider sidebar={sidebarRef}>
                    <ApplicationLayoutBottomBarContext.Provider value={bottomBarContextValue}>
                        <ApplicationLayout.Body
                            bottomBar={
                                bottomBar ? (
                                    <ApplicationLayoutBodyBottomBar buttonAlignment='right' useBodyPaddings>
                                        <BottomBarViewRefresh
                                            ariaLabel={bottomBar.ariaLabel}
                                            isRefreshing={bottomBar.isRefreshing}
                                            onRefresh={bottomBar.onRefresh}
                                            testId={bottomBar.testId}
                                            trackingKey={bottomBar.trackingKey}
                                        />
                                    </ApplicationLayoutBodyBottomBar>
                                ) : null
                            }
                        >
                            <NotificationsContainer />
                            <ErrorBoundary>
                                {error ? <ErrorFallback onRetry={refetch} /> : !isLoading && <Outlet />}
                            </ErrorBoundary>
                        </ApplicationLayout.Body>
                    </ApplicationLayoutBottomBarContext.Provider>
                </SidebarContextProvider>

                {showAnalyticsAnalysisOverlay && (
                    <Suspense fallback={null}>
                        <AnalyticsAnalysisOverlay
                            bannerStartCoordinates={{ top: Math.max(0, window.innerHeight - 85), left: 15 }}
                        />
                    </Suspense>
                )}
            </ApplicationLayout>
        </MissingPocketDriverDataSourceBannerStateProvider>
    );
};
